import "maplibre-gl/dist/maplibre-gl.css"
import { maxZoom, minZoom, zIndex } from "@nova/constants"
import { selectWeatherEnabled, useOverviewMapStylesJSON } from "features/map/control/selectors"
import { useOverlays } from "features/map/control/useOverlays"
import { DeckGLOverlay } from "features/map/MapPreview"
import {
    getViewState,
    notifyGlobalMapLoaded,
    setGlobalMapRef,
    setNewViewstate,
    updateViewport,
    useMapViewStateWriter,
} from "features/map/GlobalViewState"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useEffect, useRef, useState } from "react"
import { Map, MapRef, StyleSpecification, ViewState } from "react-map-gl/dist/maplibre"
import { RightPanel } from "./RightPanel"
import { ButtonFloating } from "@nova/component-btn"
import {
    clearActivePopup,
    closeRightPanel,
    openRightPanel,
    selectActiveAircraftId,
    selectActiveSearchPanelPlugin,
    selectIsUpdatingProjectLocation,
    selectRightPanelOpen,
} from "./slice"
import * as Icons from "@nova/component-icons"
import { ActivePopup } from "./map-popups/ActivePopup"
import { useRestrictionLayer } from "features/map/layers/airspace/layers/restriction/useRestrictionLayer"
import { useNotamLayer } from "features/map/layers/airspace/layers/notam/useNotamLayer"
import { useTfrLayer } from "features/map/layers/airspace/layers/tfr/useTfrLayer"
import { OverviewSearchArea } from "./search/OverviewSearchArea"
import { MapLegend } from "features/map/legend/MapLegend"
import { AircraftDetailPanel } from "./aircraft-detail/AircraftDetailPanel"
import { useAircraftTrackingLayers } from "features/map/layers/aircraft-tracking/layers"
import { useAdsbAreaPreviewLayer } from "features/map/layers/aircraft-tracking/useAdsbAreaPreviewLayer"
import { AdsbAreaHandle } from "./adsb/AdsbAreaHandle"
import { AdsbScanAreaLabel } from "./adsb/AdsbScanAreaLabel"
import { OverviewProjectFilter } from "./filters/OverviewProjectFilter"
import { OverviewSharingLocationFilter } from "./filters/OverviewSharingLocationFilter"
import { GeolocatedProjectMarkers } from "features/map/layers/geolocated-project/GeolocatedProjectMarkers"
import { AdsbTrafficUpdatesPanel } from "./adsb/AdsbTrafficUpdatesPanel"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import {
    selectActiveWorkspace,
    selectActiveWorkspaceId,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useOrgUserLocationSubscription } from "features/user-locations/useOrgUserLocationSubscription"
import { WeatherLayersFooterButton } from "features/map/footer/WeatherLayersFooterButton"
import { WeatherControls } from "features/map/layers/weather/controls/WeatherControls"
import { useWeatherOverviewLayers } from "features/map/layers/weather/useWeatherOverviewLayers"
import { MapFooterLayersPanel } from "features/map/footer/MapFooterLayersPanel"
import { useFireDangerLayer } from "features/map/layers/overlays/useFireDangerLayer"
import { useCivilizationLayer } from "features/map/layers/overlays/useCivilizationLayer"
import { useElevationLayer } from "features/map/layers/overlays/useElevationLayer"
import { useGeostationaryLayer } from "features/map/layers/overlays/useGeostationaryLayer"
import { useFormattedMouseCoordinate } from "features/map/search/MapSearch"
import { MapFooterScaleBar } from "features/map/footer/MapFooterScaleBar"
import ZoomControls from "features/map/footer/MapFooterZoomControls"
import { MapCompass } from "features/map/footer/Compass"
import { useMapBackground } from "features/map/control/useMapStyles"
import { isLocalhostEnvironment } from "@nova/configuration/browser"
import { Dropdown } from "@nova/component-dropdown"
import { ExpandableContent } from "@nova/component-expandable-content"
import { DiscriminatedUnion } from "@nova/util-ts"
import { Layer } from "@deck.gl/core"
import classNames from "classnames"
import { UpdateLocationModeOverlay } from "./location-override/UpdateLocationModeOverlay"
import { LocationOverrideMapLayers } from "./location-override/LocationOverrideMapLayers"

const WEATHER_PANEL_TRANSITION_MS = 250

const LAYER_HOOK_REGISTRY: DiscriminatedUnion<
    | {
          single: true
          hook: (enabled: boolean) => Layer | null
      }
    | {
          multi: true
          hook: (enabled: boolean) => (Layer | null)[]
      }
>[] = [
    { single: true, hook: useRestrictionLayer },
    { single: true, hook: useNotamLayer },
    { single: true, hook: useTfrLayer },
    { multi: true, hook: useAircraftTrackingLayers },
    { single: true, hook: useAdsbAreaPreviewLayer },
    { multi: true, hook: useWeatherOverviewLayers },
]

export const OverviewPage = () => {
    const dispatch = useAppDispatch()
    const mapStylesJSON = useOverviewMapStylesJSON()
    useOrgUserLocationSubscription()

    const trackViewState = useCallback(({ viewState }: { viewState: ViewState }) => {
        updateViewport(viewState)
    }, [])

    const mapRef = useRef<MapRef | null>(null)
    useMapViewStateWriter(mapRef)
    useEffect(() => {
        setGlobalMapRef(mapRef)
        return () => setGlobalMapRef(undefined)
    }, [])

    const [hasZoomedToLocation, setHasZoomedToLocation] = useState(false)
    const { latitude, longitude, zoom } = useAppSelector(selectActiveWorkspace())?.location ?? {}
    useEffect(() => {
        if (hasZoomedToLocation) return
        if (latitude && longitude && zoom) {
            setNewViewstate(
                { latitude, longitude, zoom, pitch: 0, bearing: 0 },
                { disableFly: true }
            )
        }
        setHasZoomedToLocation(true)
    }, [latitude, longitude, zoom, hasZoomedToLocation])

    const isUpdatingLocation = useAppSelector(selectIsUpdatingProjectLocation)

    const layers = LAYER_HOOK_REGISTRY.flatMap((registeredLayer) =>
        // DO NOT MODIFY THIS!!!!!!
        // This map is calling hooks. Since LAYER_HOOK_REGISTRY is constant, we can map over it.
        // This must NOT BE MEMOIZED inside a useMemo.
        // This must NOT BE FILTERED or the number of hooks may change between renders, which would cause a crash.
        registeredLayer.single
            ? [registeredLayer.hook(!isUpdatingLocation)]
            : registeredLayer.hook(!isUpdatingLocation)
    )

    const { fireDanger, civilization, elevation, geostationary } = useOverlays("overview")
    useFireDangerLayer(fireDanger.enabled && !isUpdatingLocation)
    useCivilizationLayer(civilization.enabled && !isUpdatingLocation)
    useElevationLayer(elevation.enabled && !isUpdatingLocation)
    useGeostationaryLayer(geostationary.enabled && !isUpdatingLocation)

    if (!hasZoomedToLocation) return null

    return (
        <div className="w-full h-full relative">
            <Map
                id="map"
                ref={mapRef}
                onMove={trackViewState}
                onLoad={notifyGlobalMapLoaded}
                initialViewState={getViewState()}
                mapStyle={mapStylesJSON as StyleSpecification}
                styleDiffing={false}
                maxZoom={maxZoom}
                minZoom={minZoom}
                maxPitch={60}
                attributionControl={false}
                logoPosition="bottom-right"
                doubleClickZoom={false}
            >
                <DeckGLOverlay
                    interleaved
                    layers={layers}
                    onClick={(info) => {
                        if (!info.object) dispatch(clearActivePopup())
                    }}
                    getCursor={({ isHovering, isDragging }) =>
                        isDragging ? "grabbing" : isHovering ? "pointer" : "grab"
                    }
                />
                {isUpdatingLocation && <LocationOverrideMapLayers />}
                {!isUpdatingLocation && (
                    <>
                        <ActivePopup />
                        <AdsbAreaHandle />
                        <AdsbScanAreaLabel />
                        <GeolocatedProjectMarkers />
                    </>
                )}
            </Map>
            {isUpdatingLocation && <UpdateLocationModeOverlay />}
            {!isUpdatingLocation && <OverviewUIOverlay />}
        </div>
    )
}

const OverviewUIOverlay = () => {
    const dispatch = useAppDispatch()
    const isRightPanelOpen = useAppSelector(selectRightPanelOpen)
    const isSearchPanelOpen = !!useAppSelector(selectActiveSearchPanelPlugin)
    const isAircraftPanelOpen = !!useAppSelector(selectActiveAircraftId)
    const { isLight } = useMapBackground("overview")
    const formattedMouseCoordinate = useFormattedMouseCoordinate()
    const [weatherMenuOpen, setWeatherMenuOpen] = useState(false)
    return (
        <div
            className="absolute inset-0 flex pointer-events-none"
            style={{ zIndex: zIndex.InspectorPanel }}
        >
            <div className="flex-1 flex flex-col justify-between pointer-events-none">
                <AircraftDetailPanel />
                <div className="flex items-start gap-8">
                    <div className="pt-[7px] pl-2 flex flex-col gap-2">
                        <OverviewSearchArea />
                        <div
                            className={classNames("transition-transform", {
                                "translate-x-[392px]": isSearchPanelOpen,
                            })}
                        >
                            <MapLegend viewType="overview" />
                        </div>
                    </div>
                    <div className="flex h-16 items-center gap-2">
                        <OverviewSharingLocationFilter />
                        <OverviewProjectFilter />
                    </div>
                    <div className="grow" />
                    <div className="p-4">
                        <ButtonFloating
                            title={isRightPanelOpen ? "Close" : "Map Options"}
                            onClick={() => {
                                if (isRightPanelOpen) {
                                    dispatch(closeRightPanel())
                                } else {
                                    dispatch(openRightPanel())
                                }
                            }}
                            RightIcon={
                                isRightPanelOpen ? Icons.LeftPanelOpen : Icons.LeftPanelClose
                            }
                            className="pointer-events-auto"
                        />
                    </div>
                </div>
                <div className="w-full flex flex-col">
                    <div className="flex justify-end p-2">
                        <AdsbTrafficUpdatesPanel />
                    </div>
                    <div className="relative mx-2">
                        <div className="w-full py-2 flex gap-4 items-center">
                            <div className="flex-1 flex items-center gap-1">
                                <MapFooterLayersPanel
                                    viewType="overview"
                                    disabled={isSearchPanelOpen || isAircraftPanelOpen}
                                />
                                <div className="relative pointer-events-auto">
                                    <WeatherLayersFooterButton
                                        isLight={isLight}
                                        isOpen={weatherMenuOpen}
                                        onOpenChange={setWeatherMenuOpen}
                                    />
                                </div>
                            </div>
                            <div
                                className={classNames("shrink-0", {
                                    "text-white": !isLight,
                                    "text-black-1000": isLight,
                                })}
                            >
                                {formattedMouseCoordinate}
                            </div>
                            <div className="flex-1 flex items-center justify-end gap-3">
                                <LocalhostDataSeeds />
                                <div className="w-[150px] h-[18px] flex items-center justify-right mt-1 overflow-y-hidden">
                                    <MapFooterScaleBar isLight={isLight} />
                                </div>
                                <ZoomControls isLight={isLight} />
                                <MapCompass isLight={isLight} />
                            </div>
                        </div>
                        <OverviewWeatherControlsRow />
                    </div>
                </div>
            </div>
            <RightPanel />
        </div>
    )
}

const OverviewWeatherControlsRow = () => {
    const weather = useAppSelector(selectWeatherEnabled)
    const [mounted, setMounted] = useState(weather)
    useEffect(() => {
        if (weather) {
            setMounted(true)
            return
        }
        const timer = setTimeout(() => setMounted(false), WEATHER_PANEL_TRANSITION_MS)
        return () => clearTimeout(timer)
    }, [weather])

    return (
        <ExpandableContent isOpen={weather} transitionDurationMs={WEATHER_PANEL_TRANSITION_MS}>
            <div className="pt-2">{mounted && <WeatherControls viewType="overview" />}</div>
        </ExpandableContent>
    )
}

const LocalhostDataSeeds = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    if (!isLocalhostEnvironment() || !orgId) return null
    return (
        <div className="pointer-events-auto">
            <Dropdown>
                <Dropdown.TriggerButton>Seed data</Dropdown.TriggerButton>
                <Dropdown.Menu position="top-right">
                    <Dropdown.ButtonItem
                        onClick={() =>
                            fetchApiGateway(
                                "post /org/:orgId/localhost-seed/airspace-restrictions",
                                {
                                    params: { orgId },
                                }
                            ).then(console.log)
                        }
                    >
                        Airspace Restrictions
                    </Dropdown.ButtonItem>
                    <Dropdown.ButtonItem
                        onClick={() =>
                            fetchApiGateway("post /org/:orgId/localhost-seed/notam-tfr-full", {
                                params: { orgId },
                            }).then(console.log)
                        }
                    >
                        NOTAM/TFR (Full)
                    </Dropdown.ButtonItem>
                    <Dropdown.ButtonItem
                        onClick={() =>
                            fetchApiGateway("post /org/:orgId/localhost-seed/notam-tfr-delta", {
                                params: { orgId },
                            }).then(console.log)
                        }
                    >
                        NOTAM/TFR (Delta)
                    </Dropdown.ButtonItem>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    )
}
